import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, refreshAuth } from "../lib/auth";

/** Sign-in landing. The whole point is a single, obvious "Sign in with
 *  Google" button - no other affordance, no distractions. When the user
 *  arrives already-authenticated (e.g. the redirect raced the /auth/me
 *  probe), we bounce them straight to the page they were headed to.
 *
 *  The "next" query string carries the originally-attempted route so a
 *  401 bounce on /capture lands the user back on /capture after sign-in,
 *  not back at /. */
export function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/";

  useEffect(() => {
    refreshAuth();
  }, []);

  useEffect(() => {
    if (auth.authenticated) {
      navigate(next, { replace: true });
    }
  }, [auth.authenticated, navigate, next]);

  const startGoogle = () => {
    window.location.href = `/auth/google?next=${encodeURIComponent(next)}`;
  };

  return (
    <div
      className="
        relative
        min-h-screen
        flex items-center justify-center
        bg-[#0c2540] bg-cover bg-center
        px-4 sm:px-6
        pt-[max(1rem,env(safe-area-inset-top))]
        pb-[max(1rem,env(safe-area-inset-bottom))]
      "
      style={{
        backgroundImage: "url('/brand/memso-banner.png')",
        minHeight: "100dvh",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0c2540]/30 via-[#0c2540]/55 to-[#0c2540]/95" />
      <div
        className="relative w-full max-w-md rounded-3xl border border-white/15 backdrop-blur-xl p-7 sm:p-10 text-center space-y-6 shadow-2xl"
        style={{ background: "rgba(12, 37, 64, 0.78)" }}
      >
        <div className="mx-auto w-20 h-20 rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/15">
          <img
            src="/icons/icon-512.png"
            alt="Memso"
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight lowercase text-white">
            memso
          </h1>
          <p className="text-sm text-sky-200/80 mt-1">
            intelligent memory and action
          </p>
        </div>

        {auth.loading ? (
          <div className="text-xs text-slate-300/80">Checking session…</div>
        ) : (
          <button
            type="button"
            onClick={startGoogle}
            aria-label="Sign in with Google"
            className="
              w-full inline-flex items-center justify-center gap-3
              rounded-xl bg-white text-[#1f1f1f]
              text-base font-medium tracking-tight
              px-5 py-3
              shadow-md ring-1 ring-black/10
              hover:bg-slate-50 active:bg-slate-100
              focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300
              transition-colors
            "
            style={{ fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif' }}
          >
            <GoogleGlyph />
            Sign in with Google
          </button>
        )}

        {/* Neutral subline. Per Waqas's spec the login page does not
            advertise the allowlist or hint at who has access; the
            server-side enforcement still runs in auth.ts. */}
        <p className="text-[11px] text-slate-300/70 leading-relaxed">
          Sign in to access your content.
        </p>
      </div>
    </div>
  );
}

/** Official Google "G" multicolour glyph. SVG is inlined so the
 *  button has no extra network dependency for first paint. The four
 *  paths are the Google brand colours in their canonical positions
 *  per https://developers.google.com/identity/branding-guidelines. */
function GoogleGlyph() {
  return (
    <svg
      viewBox="0 0 18 18"
      width="18"
      height="18"
      aria-hidden
      className="flex-shrink-0"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.8741 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8595-3.0477.8595-2.344 0-4.3286-1.5832-5.0364-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.9636 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9636 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4404 1.3459l2.5813-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9636 7.29C4.6714 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  );
}
